import React, { memo, useMemo } from 'react';

// Calculate the connection points between two nodes
const getConnectionPoints = (fromNode, toNode) => {
  // Calculate centers
  const fromCenterX = fromNode.x + fromNode.width / 2;
  const fromCenterY = fromNode.y + fromNode.height / 2;
  const toCenterX = toNode.x + toNode.width / 2;
  const toCenterY = toNode.y + toNode.height / 2;

  // Calculate angle between centers
  const angle = Math.atan2(toCenterY - fromCenterY, toCenterX - fromCenterX);

  // Determine which sides to connect
  let fromX, fromY, toX, toY;

  // From node - determine exit point
  const fromAbsAngle = Math.abs(angle);
  if (fromAbsAngle < Math.PI / 4) {
    // Right side
    fromX = fromNode.x + fromNode.width;
    fromY = fromCenterY;
  } else if (fromAbsAngle > (3 * Math.PI) / 4) {
    // Left side
    fromX = fromNode.x;
    fromY = fromCenterY;
  } else if (angle > 0) {
    // Bottom side
    fromX = fromCenterX;
    fromY = fromNode.y + fromNode.height;
  } else {
    // Top side
    fromX = fromCenterX;
    fromY = fromNode.y;
  }

  // To node - determine entry point (opposite direction)
  const toAngle = angle + Math.PI;
  const toAbsAngle = Math.abs(toAngle > Math.PI ? toAngle - 2 * Math.PI : toAngle);

  if (toAbsAngle < Math.PI / 4) {
    // Right side
    toX = toNode.x + toNode.width;
    toY = toCenterY;
  } else if (toAbsAngle > (3 * Math.PI) / 4) {
    // Left side
    toX = toNode.x;
    toY = toCenterY;
  } else if (toAngle > 0 && toAngle < Math.PI) {
    // Bottom side
    toX = toCenterX;
    toY = toNode.y + toNode.height;
  } else {
    // Top side
    toX = toCenterX;
    toY = toNode.y;
  }

  return { fromX, fromY, toX, toY };
};

// Generate path for connection
const generatePath = (fromX, fromY, toX, toY) => {
  // Calculate control points for smooth curve
  const dx = toX - fromX;
  const dy = toY - fromY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Curve intensity based on distance
  const curveOffset = Math.min(distance * 0.3, 50);

  // Determine direction for control points
  let cx1, cy1, cx2, cy2;

  if (Math.abs(dx) > Math.abs(dy)) {
    // Horizontal connection
    cx1 = fromX + curveOffset;
    cy1 = fromY;
    cx2 = toX - curveOffset;
    cy2 = toY;
  } else {
    // Vertical connection
    cx1 = fromX;
    cy1 = fromY + curveOffset;
    cx2 = toX;
    cy2 = toY - curveOffset;
  }

  return `M ${fromX} ${fromY} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${toX} ${toY}`;
};

// Calculate label position
const getLabelPosition = (fromX, fromY, toX, toY) => {
  return {
    x: (fromX + toX) / 2,
    y: (fromY + toY) / 2 - 10
  };
};

// Calculate arrow position and rotation
const getArrowTransform = (fromX, fromY, toX, toY) => {
  const angle = Math.atan2(toY - fromY, toX - fromX) * (180 / Math.PI);
  return `translate(${toX}, ${toY}) rotate(${angle})`;
};

const Connection = memo(({
  connection,
  fromNode,
  toNode,
  isSelected,
  onClick,
  onDoubleClick
}) => {
  const { id, label, style, color = '#6b7280' } = connection;

  // Calculate path points
  const pathData = useMemo(() => {
    if (!fromNode || !toNode) return null;

    const { fromX, fromY, toX, toY } = getConnectionPoints(fromNode, toNode);
    const path = generatePath(fromX, fromY, toX, toY);
    const labelPos = getLabelPosition(fromX, fromY, toX, toY);
    const arrowTransform = getArrowTransform(fromX, fromY, toX, toY);

    return { path, labelPos, arrowTransform, toX, toY };
  }, [fromNode, toNode]);

  if (!pathData) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    onClick?.(id);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    onDoubleClick?.(id);
  };

  // Use connection color, override with blue when selected
  const strokeColor = isSelected ? '#1d4ed8' : color;
  const strokeWidth = isSelected ? 3 : 2;

  // Determine dash pattern based on style
  let strokeDasharray = 'none';
  if (style === 'dashed') {
    strokeDasharray = '8,4';
  } else if (style === 'dotted') {
    strokeDasharray = '3,3';
  }

  // Generate unique marker ID for this connection's color
  const markerId = `arrowhead-${id}`;

  return (
    <g className="connection-group" onClick={handleClick} onDoubleClick={handleDoubleClick}>
      {/* Dynamic arrow marker with connection color */}
      <defs>
        <marker
          id={markerId}
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <polygon
            points="0 0, 10 3.5, 0 7"
            fill={strokeColor}
          />
        </marker>
      </defs>

      {/* Invisible wider path for easier clicking */}
      <path
        d={pathData.path}
        fill="none"
        stroke="transparent"
        strokeWidth={15}
        style={{ cursor: 'pointer' }}
      />

      {/* Visible path */}
      <path
        d={pathData.path}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        markerEnd={`url(#${markerId})`}
        style={{ cursor: 'pointer' }}
      />

      {/* Connection label */}
      {label && (
        <g transform={`translate(${pathData.labelPos.x}, ${pathData.labelPos.y})`}>
          <rect
            x={-label.length * 4 - 8}
            y={-12}
            width={label.length * 8 + 16}
            height={24}
            fill="#ffffff"
            stroke={strokeColor}
            strokeWidth={2}
            rx={12}
          />
          <text
            x={0}
            y={4}
            textAnchor="middle"
            fontSize="12"
            fontWeight="600"
            fill={color}
            fontFamily="Inter, system-ui, sans-serif"
          >
            {label}
          </text>
        </g>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <circle
          cx={pathData.labelPos.x}
          cy={pathData.labelPos.y + (label ? 0 : 10)}
          r={6}
          fill="#3b82f6"
          stroke="#fff"
          strokeWidth={2}
        />
      )}
    </g>
  );
});

Connection.displayName = 'Connection';

export default Connection;
